import { useState } from "react"
import { Badge, Card, CardBody, Col, Label, Row, Table } from "reactstrap"
import Select from "react-select"

import PageWrapper from "components/Common/PageWrapper"
import apiClient from "api/axios"
import { useAttendanceLeaderboard } from "hooks/useAttendanceLeaderboard"

const downloadBlob = (blobData, filename) => {
  const url = window.URL.createObjectURL(new Blob([blobData]))
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

const MONTHS = [
  { value: 1, label: "January" }, { value: 2, label: "February" },
  { value: 3, label: "March" }, { value: 4, label: "April" },
  { value: 5, label: "May" }, { value: 6, label: "June" },
  { value: 7, label: "July" }, { value: 8, label: "August" },
  { value: 9, label: "September" }, { value: 10, label: "October" },
  { value: 11, label: "November" }, { value: 12, label: "December" },
]

const buildYearOptions = () => {
  const current = new Date().getFullYear()
  return Array.from({ length: 5 }, (_, i) => current - 2 + i)
}

const scoreColor = score => {
  if (score === null || score === undefined) return "secondary"
  if (score >= 80) return "success"
  if (score >= 60) return "warning"
  return "danger"
}

const RANK_MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" }

const RankBadge = ({ rank }) => (
  <span className="fw-bold">
    {RANK_MEDAL[rank] ?? `#${rank}`}
  </span>
)

const TopThree = ({ top }) => (
  <Row className="text-center mb-4">
    {top.map(entry => (
      <Col md={4} key={entry.id} className="mb-3 mb-md-0">
        <Card className="h-100">
          <CardBody>
            <div className="fs-2 mb-1">{RANK_MEDAL[entry.rank] ?? `#${entry.rank}`}</div>
            <div className="fw-semibold">{entry.name}</div>
            <Badge color={scoreColor(entry.score)} pill className="mt-2">
              {entry.score}
            </Badge>
          </CardBody>
        </Card>
      </Col>
    ))}
  </Row>
)

const MyRankCard = ({ me }) => {
  if (!me) return null

  return (
    <Card className="mb-4 border-primary">
      <CardBody className="d-flex align-items-center justify-content-between">
        <div>
          <div className="text-muted small">Your rank</div>
          <div className="fw-bold fs-5">
            <RankBadge rank={me.rank} /> {me.name}
          </div>
        </div>
        <Badge color={scoreColor(me.score)} pill className="fs-6">
          {me.score}
        </Badge>
      </CardBody>
    </Card>
  )
}

const AttendanceLeaderboard = () => {
  document.title = "Attendance Leaderboard"

  const today = new Date()
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [year, setYear] = useState(today.getFullYear())
  const [yearOptions] = useState(buildYearOptions())
  const yearSelectOptions = yearOptions.map(y => ({ value: y, label: String(y) }))

  const { data, isLoading, isFetching, error, refetch } = useAttendanceLeaderboard(month, year)
  const report = data?.data
  const top = report?.top ?? []
  const rows = report?.rows ?? []
  const canViewAll = report?.can_view_all ?? false

  const [isDownloading, setIsDownloading] = useState(false)

  const downloadLeaderboardPdf = async () => {
    setIsDownloading(true)
    try {
      const res = await apiClient.get("/kpi/attendance-leaderboard/pdf", {
        params: { month, year },
        responseType: "blob",
      })
      downloadBlob(res.data, `attendance-leaderboard-${year}-${String(month).padStart(2, "0")}.pdf`)
    } catch (err) {
      console.error("Attendance leaderboard PDF download failed", err)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <PageWrapper title="Attendance Leaderboard">
      <Card className="mb-3">
        <CardBody>
          <Row className="align-items-end">
            <Col md={2}>
              <Label>Month</Label>
              <Select
                options={MONTHS}
                value={MONTHS.find(m => m.value === month) || null}
                onChange={selected => setMonth(selected ? selected.value : month)}
                placeholder="Search month…"
              />
            </Col>
            <Col md={2}>
              <Label>Year</Label>
              <Select
                options={yearSelectOptions}
                value={yearSelectOptions.find(y => y.value === year) || null}
                onChange={selected => setYear(selected ? selected.value : year)}
                placeholder="Search year…"
              />
            </Col>
            <Col md={2}>
              <button className="btn btn-primary" onClick={() => refetch()} disabled={isFetching}>
                {isFetching ? "Refreshing…" : "Refresh"}
              </button>
            </Col>
            <Col md={2}>
              <button
                type="button"
                className="btn btn-outline-success"
                onClick={downloadLeaderboardPdf}
                disabled={isDownloading || !report}
              >
                <i className="bx bx-download me-1"></i>
                {isDownloading ? "ডাউনলোড হচ্ছে…" : "PDF ডাউনলোড"}
              </button>
            </Col>
            {report?.is_current_month && (
              <Col className="text-end">
                <Badge color="info">Live (in-progress month)</Badge>
              </Col>
            )}
          </Row>
          {error && (
            <div className="alert alert-danger mt-3 mb-0 small">
              Failed to load attendance leaderboard. {error?.response?.data?.message ?? error?.message}
            </div>
          )}
        </CardBody>
      </Card>

      {isLoading && !report && (
        <div className="text-center text-muted py-4">Loading leaderboard…</div>
      )}

      {report && (
        <>
          {top.length > 0 && <TopThree top={top} />}

          <MyRankCard me={report.me} />

          {canViewAll ? (
            <Card>
              <CardBody className="p-0">
                <div style={{ overflowX: "auto" }}>
                  <Table responsive bordered className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 80 }}>Rank</th>
                        <th>Employee</th>
                        <th className="text-center">Attendance Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(row => (
                        <tr key={row.id}>
                          <td><RankBadge rank={row.rank} /></td>
                          <td>
                            {row.name}
                            <br />
                            <span className="text-muted small">{row.employee_id}</span>
                          </td>
                          <td className="text-center">
                            <Badge color={scoreColor(row.score)} pill>
                              {row.score}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={3} className="text-center text-muted py-4">
                            No attendance data for this period.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          ) : (
            <div className="alert alert-secondary small mb-0">
              You don't have permission to view the full leaderboard — showing the top performers and your own rank only.
            </div>
          )}
        </>
      )}
    </PageWrapper>
  )
}

export default AttendanceLeaderboard
